import { useEffect, useRef, useState } from "react";

// Single shared <audio> instance so only one jingle ever plays at a time.
let sharedAudio: HTMLAudioElement | null = null;
const listeners = new Set<(currentSrc: string | null, playing: boolean) => void>();

function getAudio(): HTMLAudioElement {
  if (!sharedAudio) {
    sharedAudio = new Audio();
    sharedAudio.preload = "none";
    sharedAudio.addEventListener("play", () => emit());
    sharedAudio.addEventListener("pause", () => emit());
    sharedAudio.addEventListener("ended", () => emit());
  }
  return sharedAudio;
}

function emit() {
  const a = sharedAudio;
  const src = a && !a.paused ? a.currentSrc : null;
  const playing = !!(a && !a.paused);
  listeners.forEach((l) => l(src, playing));
}

export interface JinglePlayerProps {
  src: string;
  title: string;
}

export default function JinglePlayer({ src, title }: JinglePlayerProps) {
  const [isActive, setIsActive] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [time, setTime] = useState("0:00");
  const [duration, setDuration] = useState("");
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const listener = (currentSrc: string | null, playing: boolean) => {
      const mine = currentSrc === src || (currentSrc && currentSrc.endsWith(src));
      setIsActive(!!mine);
      setIsPlaying(!!mine && playing);
      if (!mine) {
        setProgress(0);
        setTime("0:00");
      }
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [src]);

  useEffect(() => {
    const tick = () => {
      const a = sharedAudio;
      if (a && isActive && !a.paused) {
        const pct = a.duration ? (a.currentTime / a.duration) * 100 : 0;
        setProgress(pct);
        setTime(formatTime(a.currentTime));
        if (a.duration && !duration) setDuration(formatTime(a.duration));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isActive, duration]);

  const handleToggle = async () => {
    const a = getAudio();
    if (isActive && !a.paused) {
      a.pause();
      return;
    }
    if (!isActive) {
      a.src = src;
    }
    try {
      setIsLoading(true);
      await a.play();
    } catch {
      // user-gesture or network error — ignore
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const a = sharedAudio;
    if (!isActive || !a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    a.currentTime = ratio * a.duration;
  };

  return (
    <div className="flex items-center gap-3 mt-3">
      <button
        type="button"
        onClick={handleToggle}
        aria-label={isPlaying ? `Pause ${title}` : `Play ${title}`}
        className="flex-shrink-0 w-11 h-11 rounded-full bg-cta-blue hover:bg-cta-blue-hover text-white flex items-center justify-center transition shadow-md"
      >
        {isLoading ? (
          <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        ) : isPlaying ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div
          onClick={handleSeek}
          className="h-2 w-full bg-muted rounded-full overflow-hidden cursor-pointer"
        >
          <div
            className="h-full bg-foreground transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between mt-1 text-xs text-muted-foreground tabular-nums">
          <span>{time}</span>
          <span>{duration || "—"}</span>
        </div>
      </div>
    </div>
  );
}

function formatTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
